import { useCallback, useEffect, useRef, useState } from 'react';

import type { Pagination, ProductData } from '@shared/dream-components';
import { useQuery } from '@tanstack/react-query';

import type { Product } from '~/interfaces/product';

import { fetchProducts, productsKeys } from './useLoaders/useProducts';

export const calculateBackendPage = (displayPage: number, pagesSkipped: number): number => {
  if (!displayPage || displayPage <= 0) return 1;
  if (!pagesSkipped || pagesSkipped <= 0) return displayPage;
  const result = displayPage + pagesSkipped;
  return Number.isNaN(result) ? displayPage : result;
};

interface Arguments {
  defaultProducts: ProductData[];
  pagination: Pagination | null;
  enabled?: boolean;
  productGroupType: string;
  hasFakeData: boolean;
  pagesSkippedByOffset?: number;
  blockId?: string;
  displayPerPage?: number;
  fetchPerPage?: number;
  productTagIds?: string[];
}

export const transformToProductData = (products: Product[]) =>
  products.map(
    (product: Product) =>
      ({
        id: product.id,
        name: product.name,
        description: product.description,
        slug: product.slug,
        status: product.status,
        product_type: product.product_type,
        free: product.free,
        cta_text: product.cta_text,
        default_variant: product.default_product_variant,
        product_variants: product.product_variants,
        created_at: product.created_at,
      }) as ProductData,
  );

const usePaginatedProducts = ({
  defaultProducts = [],
  pagination,
  enabled = true,
  pagesSkippedByOffset,
  blockId,
  displayPerPage,
  fetchPerPage,
  productTagIds,
}: Arguments) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [accumulatedProducts, setAccumulatedProducts] = useState<ProductData[]>(defaultProducts);
  const paginationModeRef = useRef<'load_more' | 'go_to_page' | null>(null);
  const [pageToFetch, setPageToFetch] = useState<number | null>(null);
  const bufferRef = useRef<ProductData[]>([]);
  const [bufferLength, setBufferLength] = useState(0);

  const perPage = fetchPerPage || pagination?.per_page;
  const shouldBuffer = Boolean(displayPerPage && perPage && displayPerPage < perPage);

  const { data, isFetching } = useQuery({
    queryKey: productsKeys.page(blockId, pageToFetch || 0, perPage || 0, productTagIds),
    queryFn: () => fetchProducts(pageToFetch ?? 0, perPage || 0, productTagIds),
    enabled: enabled && pageToFetch !== null && pageToFetch > 0,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!data) return;

    const newProducts = transformToProductData(data.products);

    if (paginationModeRef.current === 'go_to_page') {
      setAccumulatedProducts(newProducts);
      bufferRef.current = [];
      setBufferLength(0);
      setCurrentPage(data.pagination?.page || 1);
    } else {
      setAccumulatedProducts((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));
        const dedupedNew = [...bufferRef.current, ...newProducts].filter((p) => !existingIds.has(p.id));

        if (shouldBuffer && displayPerPage) {
          const toShow = dedupedNew.slice(0, displayPerPage);
          bufferRef.current = dedupedNew.slice(displayPerPage);
          setBufferLength(bufferRef.current.length);
          return [...prev, ...toShow];
        }

        bufferRef.current = [];
        setBufferLength(0);
        return [...prev, ...dedupedNew];
      });

      setCurrentPage((prev) => data.pagination?.page || prev + 1);
    }

    paginationModeRef.current = null;
  }, [data, shouldBuffer, displayPerPage]);

  const loadMoreProducts = useCallback(() => {
    if (!enabled) return;

    if (shouldBuffer && displayPerPage && bufferRef.current.length >= displayPerPage) {
      const toShow = bufferRef.current.slice(0, displayPerPage);
      bufferRef.current = bufferRef.current.slice(displayPerPage);
      setBufferLength(bufferRef.current.length);
      setAccumulatedProducts((prev) => [...prev, ...toShow]);
      return;
    }

    paginationModeRef.current = 'load_more';
    const nextPage = currentPage + 1;
    setPageToFetch(nextPage);
  }, [currentPage, enabled, shouldBuffer, displayPerPage]);

  const goToPage = useCallback(
    (page: number) => {
      if (!enabled) return;

      paginationModeRef.current = 'go_to_page';
      const backendPage = calculateBackendPage(page, pagesSkippedByOffset || 0);
      setPageToFetch(backendPage);
    },
    [enabled, pagesSkippedByOffset],
  );

  const totalPages = data?.pagination?.total_pages ?? pagination?.total_pages ?? 0;

  return {
    products: accumulatedProducts,
    showLoadMore: currentPage + 1 <= totalPages || bufferLength > 0,
    isLoading: isFetching,
    loadMoreProducts,
    goToPage,
    currentPage,
  };
};

export default usePaginatedProducts;
